import React from 'react';
import { ArrowTurnUpIcon, Box, Inscription, PayoutRuleType, Text } from '@common-fe/common-fe';

import { PAYOUT_RULE_OPTIONS } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/TierItem/tierItem.types';
import { RenewalDeductibleConfig, RenewalProcessingTier } from '@/modules/Renewals/renewals.types';
import { getTierTriggerNames } from '@/modules/Renewals/RenewalSetup/utils/getTierTriggerNames';
import { isEmbeddedOnlyTier } from '@/modules/Renewals/RenewalSetup/utils/isEmbeddedOnlyTier';
import theme from '@/styles/theme';

import TierTitle from '../../HealthPlanSummaryCard/components/TierTitle';

import TiersSectionItem from './TiersSectionItem';

interface Props {
  tiers: RenewalProcessingTier[];
  testId?: string;
  deductibleConfigs?: RenewalDeductibleConfig[];
}

const TiersSection: React.FC<Props> = ({ tiers, testId = 'edit-amounts-modal', deductibleConfigs }) => {
  if (!tiers || tiers.length === 0) return null;

  return (
    <Box background="module" round="moduleRound" pad="spacing24" gap="spacing4">
      <Inscription size="20px" color="textTitle" weight={700} lineHeight="28px">
        Tiers
      </Inscription>
      {tiers.map((tier, tierIndex) => {
        const isLastUnlockTier =
          tierIndex === tiers.length - 1 && (tier.cardPurseLockPlanNames || []).length === 0;
        // Hide the "Lock card purse" block for the last unlock tier and for
        // EMBEDDED-only tiers (the block is meaningful only for non-embedded triggers).
        const isLockCardPurseHidden = isLastUnlockTier || isEmbeddedOnlyTier(tier, deductibleConfigs);
        const triggerNames = getTierTriggerNames(tier, deductibleConfigs);
        return (
          <Box key={`${tier.name}-${tierIndex}`} data-testid={`${testId}_tier-${tierIndex}`} gap="spacing12">
            <TierTitle
              tier={tier}
              tierIndex={tierIndex}
              textStyle={{ fontSize: '16px', fontWeight: 700, lineHeight: '22px' }}
            />
            {/* Use a gap so spacing appears only between rendered blocks; a hidden
                Lock card purse block (e.g. embedded-only tier) leaves no trailing gap. */}
            <Box gap="spacing12">
              {/* Triggers (shown only when the tier has triggers) */}
              {triggerNames.length > 0 && (
                <Box
                  pad="spacing16"
                  border={{ size: 'small', color: 'border1' }}
                  round="container2Round"
                  background="canvas"
                  gap="spacing2"
                >
                  <Text weight={700} color="textBody">
                    Triggers:
                  </Text>
                  <Text color="textBody">{triggerNames.join(' or ')}</Text>
                </Box>
              )}

              {/* Payout rules */}
              <Box
                pad="spacing16"
                border={{ size: 'small', color: 'border1' }}
                round="container2Round"
                background="canvas"
                gap="spacing2"
              >
                <Text weight={700} color="textBody">
                  Payout rules:
                </Text>
                <Text color="textBody">
                  {PAYOUT_RULE_OPTIONS.find((opt) => opt.key === tier.payoutRuleType)?.value ||
                    PAYOUT_RULE_OPTIONS.find((opt) => opt.key === PayoutRuleType.NO_EXCEPTIONS)?.value}
                </Text>
              </Box>

              {/* Lock card purse (hidden for the last tier with "Unlock" status,
                  and for EMBEDDED-only tiers where the block is not meaningful) */}
              {!isLockCardPurseHidden && (
                <Box
                  pad="spacing16"
                  border={{ size: 'small', color: 'border1' }}
                  round="container2Round"
                  background="canvas"
                  gap="spacing2"
                >
                  <Text weight={700} color="textBody">
                    Lock card purse:
                  </Text>
                  <Text color="textBody">
                    {(tier.cardPurseLockPlanNames || []).length > 0
                      ? `Lock (${(tier.cardPurseLockPlanNames || []).join(', ')})`
                      : 'Unlock'}
                  </Text>
                </Box>
              )}

              {/* Services */}
              {(tier.serviceConfigs || []).length > 0 && (
                <Box margin={{ left: 'spacing24' }}>
                  <Box direction="row" align="start" margin={{ bottom: 'spacing4' }}>
                    <ArrowTurnUpIcon
                      color="iconSecondary"
                      size="12px"
                      style={{ marginTop: theme.spacings.spacing2 }}
                    />
                    <Text size="small" color="textSecondary" weight={500} margin={{ left: 'spacing4' }}>
                      Services
                    </Text>
                  </Box>
                  <Box gap="spacing8">
                    {(tier.serviceConfigs || []).map((service, serviceIndex) => (
                      <TiersSectionItem
                        key={`${tier.name}-${service.serviceName}-${service.paymentType}`}
                        service={service}
                        serviceIndex={serviceIndex}
                        tierIndex={tierIndex}
                        testId={testId}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {tierIndex !== tiers.length - 1 && (
                <Box height="1px" background="border1" margin={{ bottom: 'spacing8' }} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default TiersSection;
